import { Box, Checkbox, Group, Stack, Table, Text } from "@mantine/core"
import { ChatCenteredTextIcon, MapTrifoldIcon } from "@phosphor-icons/react"
import dayjs from "dayjs"
import { Fragment } from "react"
import { useTranslation } from "react-i18next"
import { IDeliveryNotePlus } from "../../models/DeliveryNote"
import { formatReceiverAddress } from "../../utils/format-branch-address"
import { renderBusinessName } from "../../utils/render-business-name"
import { DeliveryRowAction } from "../delivery/action-controls"
import RenderText from "../ui/render-text"
import { CodeStack, DotPill, TableFrame, Tone } from "../delivery/atoms"
import { useDeliveryActions } from "../delivery/delivery-actions"
import { computeSla } from "./atoms"
import { PendingRowMeta } from "./hooks"
import classes from "./pending.module.css"

interface Props {
  rows: IDeliveryNotePlus[]
  selectedIds: string[]
  onToggleSelect: (id: string) => void
  onToggleSelectAll: () => void
  onAssignOne: (dn: IDeliveryNotePlus) => void
  onProgressNote?: (dn: IDeliveryNotePlus) => void
  onViewDetail?: (dn: IDeliveryNotePlus) => void
  metaByDn: Map<string, PendingRowMeta>
  countsByDistrict: Map<string, number>
  footer?: React.ReactNode
}

const SEVERITY_TONE: Record<string, Tone> = { OK: "green", WARNING: "amber", CRITICAL: "red" }

function weightKg(dn: IDeliveryNotePlus): number | null {
  const fromServer = (dn as { totalWeightKg?: number | null }).totalWeightKg
  if (typeof fromServer === "number" && fromServer > 0) return fromServer
  let sum = 0
  for (const l of (dn.lines ?? []) as Array<{ weight?: number | null; quantity?: number | null }>) {
    if (l.weight != null && l.weight > 0) sum += l.weight * (l.quantity ?? 0)
  }
  return sum > 0 ? sum : null
}

function timeWindow(dn: IDeliveryNotePlus): string | null {
  if (dn.loadingDate && dn.deliveryDate) {
    const start = dayjs(dn.loadingDate)
    const end = dayjs(dn.deliveryDate)
    return start.isSame(end, "day")
      ? `${start.format("HH:mm")}-${end.format("HH:mm")}`
      : `${start.format("MM-DD HH:mm")} → ${end.format("MM-DD HH:mm")}`
  }
  return dn.deliveryDate ? dayjs(dn.deliveryDate).format("MM-DD HH:mm") : null
}

/** Day key of the delivery date (DN's own, else the order's); rows arrive sorted by the same value. */
function dayKey(dn: IDeliveryNotePlus): string {
  const date = dn.deliveryDate ?? dn.orderDeliveryDate
  return date ? dayjs(date).format("YYYY-MM-DD") : ""
}

/** Хүлээлтэнд table (canvas Ord_DeliveryHub): checkbox, «Үйлдэл», DN/SO, customer, type, load, deadline + need. */
export function PendingDeliveriesTable({
  rows,
  selectedIds,
  onToggleSelect,
  onToggleSelectAll,
  onAssignOne,
  onProgressNote,
  onViewDetail,
  metaByDn,
  footer,
}: Props) {
  const { t } = useTranslation("delivery")
  const { stateOf } = useDeliveryActions()
  const allSelected = rows.length > 0 && rows.every((r) => r.id && selectedIds.includes(r.id))
  const someSelected = !allSelected && rows.some((r) => r.id && selectedIds.includes(r.id))

  const countByDay = new Map<string, number>()
  for (const r of rows) countByDay.set(dayKey(r), (countByDay.get(dayKey(r)) ?? 0) + 1)

  const dayLabel = (day: string): string | null => {
    const today = dayjs().startOf("day")
    const d = dayjs(day)
    if (d.isSame(today, "day")) return t("manage.pending.dateGroupToday")
    if (d.isSame(today.add(1, "day"), "day")) return t("manage.pending.dateGroupTomorrow")
    return null
  }

  const remaining = (ms: number) => {
    if (ms <= 0) return t("manage.sla.overdue")
    const minutes = Math.floor(ms / 60000)
    const days = Math.floor(minutes / 1440)
    const hours = Math.floor((minutes % 1440) / 60)
    if (days >= 1) return t("manage.sla.days", { d: days, h: hours })
    if (hours >= 1) return t("manage.sla.hours", { h: hours, m: minutes % 60 })
    return t("manage.sla.minutes", { m: minutes })
  }

  return (
    <TableFrame footer={footer}>
      <Table.ScrollContainer minWidth={1040}>
        <Table highlightOnHover verticalSpacing="sm" horizontalSpacing="md">
          <Table.Thead>
            <Table.Tr>
              <Table.Th w={44}>
                <Checkbox
                  checked={allSelected}
                  indeterminate={someSelected}
                  onChange={onToggleSelectAll}
                  aria-label={t("manage.pending.selectAll")}
                />
              </Table.Th>
              <Table.Th w={100}>{t("manage.col.action")}</Table.Th>
              <Table.Th>{t("manage.col.dnOrder")}</Table.Th>
              <Table.Th>{t("manage.col.customerAddress")}</Table.Th>
              <Table.Th>{t("manage.col.type")}</Table.Th>
              <Table.Th>{t("manage.col.load")}</Table.Th>
              <Table.Th>{t("manage.col.deadlineNeed")}</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {rows.map((dn, index) => {
              const id = dn.id ?? ""
              const day = dayKey(dn)
              const startsDay = index === 0 || dayKey(rows[index - 1]) !== day
              const isSelected = id ? selectedIds.includes(id) : false
              const meta = metaByDn.get(id)
              const priority = meta?.priority ?? "NORMAL"
              const sla = computeSla(dn.deliveryDate, dn.createdAt)
              const channel = dn.classification?.deliveryChannel ?? null
              const subject = { deliveryNoteStatus: dn.deliveryNoteStatus, deliveryChannel: channel }
              const addState = stateOf("addToRoute", subject)
              const notifyState = stateOf("notifyBuyer", subject)
              const lineCount = (dn as { lineCount?: number }).lineCount ?? dn.lines?.length ?? 0
              const size = dn.classification?.totalSizeValue ?? null
              const weight = weightKg(dn)
              const time = timeWindow(dn)
              const needs = [
                t(`manage.priority.${priority}`),
                (meta?.totalSizeNumeric ?? 0) >= 10 ? t("manage.pending.needMediumTruck") : null,
                priority !== "NORMAL" && time ? t("manage.pending.timeWindow", { time }) : null,
              ].filter(Boolean)
              const address = formatReceiverAddress(dn.receiverBranch, dn.receiverWarehouse)

              return (
                <Fragment key={id || dn.refCode}>
                  {startsDay && (
                    <Table.Tr className={classes.dateRow}>
                      <Table.Td colSpan={7}>
                        <RenderText
                          fontWeight={700}
                          color="var(--nw-text-2)"
                          fz="var(--nw-fz-body)"
                          text={[
                            day ? dayjs(day).format("MM/DD") : t("manage.pending.dateGroupNone"),
                            day ? dayLabel(day) : null,
                            t("manage.pending.dateGroupCount", { count: countByDay.get(day) ?? 0 }),
                          ]
                            .filter(Boolean)
                            .join(" · ")}
                        />
                      </Table.Td>
                    </Table.Tr>
                  )}
                  <Table.Tr
                    className={classes.row}
                    data-selected={isSelected || undefined}
                    onClick={onViewDetail ? () => onViewDetail(dn) : undefined}
                  >
                    <Table.Td onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        checked={isSelected}
                        onChange={() => id && onToggleSelect(id)}
                        aria-label={t("manage.pending.selectOne", { code: dn.refCode })}
                      />
                    </Table.Td>
                    <Table.Td onClick={(e) => e.stopPropagation()}>
                      <Group gap="xs" wrap="nowrap">
                        {addState.applies && (
                          <DeliveryRowAction
                            label={t("manage.action.addToRoute")}
                            icon={<MapTrifoldIcon />}
                            state={addState}
                            onClick={() => onAssignOne(dn)}
                          />
                        )}
                        {onProgressNote && notifyState.applies && (
                          <DeliveryRowAction
                            label={t("manage.action.notifyBuyer")}
                            icon={<ChatCenteredTextIcon />}
                            state={notifyState}
                            onClick={() => onProgressNote(dn)}
                          />
                        )}
                      </Group>
                    </Table.Td>
                    <Table.Td>
                      <CodeStack primary={dn.refCode} secondary={dn.salesCode} />
                    </Table.Td>
                    <Table.Td maw={280}>
                      <Text fw={600} fz="var(--nw-fz-body)">
                        {renderBusinessName(dn.receiverBusiness, "-")}
                      </Text>
                      <Text fz="var(--nw-fz-body)" c="var(--nw-text-2)" className={classes.clamp}>
                        {address || dn.receiverBranch?.name || "-"}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text fz="var(--nw-fz-body)">{t(`badges.channel.${channel ?? "unknown"}`)}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Text fz="var(--nw-fz-body)">
                        {t("manage.pending.loadCount", { lines: lineCount, qty: dn.totalItemCount ?? 0 })}
                      </Text>
                      {(size || weight != null) && (
                        <Text ff="monospace" fz="var(--nw-fz-mono)" c="var(--nw-text-2)">
                          {[size, weight != null ? `${weight.toFixed(weight % 1 === 0 ? 0 : 1)} ${t("manage.unit.kg")}` : null]
                            .filter(Boolean)
                            .join(" · ")}
                        </Text>
                      )}
                    </Table.Td>
                    <Table.Td>
                      <Stack gap={4} align="flex-start">
                        <DotPill tone={SEVERITY_TONE[sla.severity]}>{remaining(sla.remainingMs)}</DotPill>
                        <Text fz="var(--nw-fz-body)" c="var(--nw-text-2)">
                          {needs.join(" · ")}
                        </Text>
                      </Stack>
                    </Table.Td>
                  </Table.Tr>
                </Fragment>
              )
            })}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>
      {rows.length === 0 && <Box p="xl" />}
    </TableFrame>
  )
}
